/**
 * `NeighborsTab` — 1-hop neighbour preview with client-side filter + staged
 * add. Lazy loads via `useEntityNeighbors` the moment this component mounts
 * (Radix lazy-mounts inactive `TabsContent` children, so this fires only when
 * the user clicks the Neighbours tab).
 *
 * Row layout mirrors the Bloom-style mockup:
 *   [icon] [name + edge-type pill + dir] [staged toggle]
 *
 * Filter matches case-insensitively against name, primary type, edge type,
 * direction, and every value in the neighbour's properties bag. "Select all"
 * is context-aware: with a filter it stages only filtered rows and preserves
 * staged-but-hidden rows on "Clear selection". See design D7a* for details.
 */

import { AlertCircle, ArrowRight, Plus, RefreshCcw, Search, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import {
  type NeighborsResponse,
  useEntityNeighbors,
} from '@/components/neo4j/shared/useEntityNeighbors'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils/cn'
import type { NeighborEdge, NeighborNode, NeighborRow } from './types'

const VIRTUALISATION_THRESHOLD = 50
const WINDOW_STEP = 50

interface LegendLabel {
  label: string
  count: number
  color: string
  borderColor?: string
}

export interface NeighborsTabProps {
  caseId: string
  nodeId: string | null
  legendLabels?: LegendLabel[]
  onAddToGraph: (nodes: NeighborNode[], edges: NeighborEdge[]) => void
}

function matchesNeighbor(row: NeighborRow, query: string): boolean {
  if (!query) return true
  const needle = query.toLowerCase()
  const primaryType = row.neighbor.labels[0] ?? row.neighbor.type ?? ''
  const haystack: string[] = [
    row.neighbor.name,
    primaryType,
    row.edgeType,
    row.direction,
    ...Object.values(row.neighbor.properties ?? {}).map(v => String(v ?? '')),
  ]
  return haystack.some(h => h.toLowerCase().includes(needle))
}

function labelStyle(label: string, legend: LegendLabel[] | undefined): React.CSSProperties {
  const hit = legend?.find(l => l.label === label)
  if (hit) {
    return {
      backgroundColor: hit.color,
      color: 'var(--primary-foreground)',
      borderColor: hit.borderColor ?? hit.color,
    }
  }
  const colors = LABEL_COLORS[label] ?? LABEL_COLORS.Default
  if (!colors) {
    return { backgroundColor: 'var(--muted)', color: 'var(--foreground)' }
  }
  return { backgroundColor: colors.fill, color: '#ffffff', borderColor: colors.stroke }
}

function buildRows(nodeId: string, data: NeighborsResponse | null): NeighborRow[] {
  if (!data || !nodeId) return []
  const byId = new Map<string, NeighborNode>(data.nodes.map(n => [n.id, n]))
  const rows: NeighborRow[] = []
  for (const edge of data.relationships) {
    const isOutgoing = edge.source === nodeId
    const isIncoming = edge.target === nodeId
    if (!isOutgoing && !isIncoming) continue
    const peerId = isOutgoing ? edge.target : edge.source
    if (peerId === nodeId) continue
    const peer = byId.get(peerId)
    if (!peer) continue
    rows.push({
      edgeId: edge.id,
      edgeType: edge.type,
      edgeProperties: edge.properties,
      direction: isOutgoing ? 'outgoing' : 'incoming',
      neighbor: peer,
    })
  }
  return rows.sort((a, b) => {
    if (a.edgeType !== b.edgeType) return a.edgeType.localeCompare(b.edgeType)
    return a.neighbor.name.localeCompare(b.neighbor.name)
  })
}

export function NeighborsTab({ caseId, nodeId, legendLabels, onAddToGraph }: NeighborsTabProps) {
  const { data, isLoading, error, refetch } = useEntityNeighbors({ caseId, nodeId })
  const [query, setQuery] = useState('')
  const [staged, setStaged] = useState<Set<string>>(() => new Set())
  const [windowCount, setWindowCount] = useState(WINDOW_STEP)

  // Clear staging + grow-window counter whenever the selected node changes.
  useEffect(() => {
    setStaged(new Set())
    setWindowCount(WINDOW_STEP)
  }, [nodeId])

  const allRows = useMemo(() => buildRows(nodeId ?? '', data), [nodeId, data])

  const visibleRows = useMemo(
    () => allRows.filter(r => matchesNeighbor(r, query)),
    [allRows, query]
  )

  const shouldVirtualise = visibleRows.length > VIRTUALISATION_THRESHOLD
  const renderedRows = shouldVirtualise ? visibleRows.slice(0, windowCount) : visibleRows
  const hiddenWindowCount = shouldVirtualise ? visibleRows.length - renderedRows.length : 0

  const toggleStaged = useCallback((peerId: string) => {
    setStaged(prev => {
      const next = new Set(prev)
      if (next.has(peerId)) next.delete(peerId)
      else next.add(peerId)
      return next
    })
  }, [])

  const visibleIds = useMemo(() => new Set(visibleRows.map(r => r.neighbor.id)), [visibleRows])
  const allVisibleStaged =
    visibleRows.length > 0 && visibleRows.every(r => staged.has(r.neighbor.id))
  const hiddenStagedCount = Array.from(staged).filter(id => !visibleIds.has(id)).length

  const selectAllLabel = allVisibleStaged
    ? 'Clear selection'
    : query
      ? 'Select all matching'
      : 'Select all'

  const onSelectAllClick = useCallback(() => {
    if (allVisibleStaged) {
      setStaged(prev => {
        const next = new Set(prev)
        for (const id of visibleIds) next.delete(id)
        return next
      })
    } else {
      setStaged(prev => {
        const next = new Set(prev)
        for (const id of visibleIds) next.add(id)
        return next
      })
    }
  }, [allVisibleStaged, visibleIds])

  const onConfirm = useCallback(() => {
    if (!data || staged.size === 0) return
    const nodesToAdd = data.nodes.filter(n => staged.has(n.id))
    const keepIds = new Set<string>(nodesToAdd.map(n => n.id))
    const edgesToAdd = data.relationships.filter(
      e => keepIds.has(e.source) || keepIds.has(e.target)
    )
    onAddToGraph(nodesToAdd, edgesToAdd)
    setStaged(new Set())
  }, [data, staged, onAddToGraph])

  if (!nodeId) return null

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="h-10 animate-pulse rounded-md border border-border bg-muted/30" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-center text-xs text-destructive">
        <AlertCircle className="h-4 w-4" />
        <span>Failed to load neighbours.</span>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={refetch}>
          <RefreshCcw className="mr-1 h-3 w-3" />
          Retry
        </Button>
      </div>
    )
  }

  if (allRows.length === 0) {
    return (
      <p className="rounded-md border border-border bg-muted/20 py-6 text-center text-xs text-muted-foreground">
        No neighbours.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Filter */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Filter by name, type, property…"
          className="h-9 pl-8 pr-9 text-xs"
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear filter"
            className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      {/* Bulk actions */}
      <div className="flex items-center justify-between gap-3">
        <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs">
          <Checkbox
            checked={allVisibleStaged}
            onCheckedChange={() => onSelectAllClick()}
            disabled={visibleRows.length === 0}
            aria-label={selectAllLabel}
          />
          <span className="font-medium text-foreground">{selectAllLabel}</span>
          <span className="tabular-nums text-muted-foreground">({visibleRows.length})</span>
        </label>
        <Button
          size="sm"
          className="h-8 gap-1.5 px-3 text-xs"
          disabled={staged.size === 0}
          onClick={onConfirm}
        >
          <Plus className="h-3.5 w-3.5" />
          Add to graph
          <span className="tabular-nums opacity-80">({staged.size})</span>
        </Button>
      </div>

      {/* Filter hint when staged rows are hidden */}
      {hiddenStagedCount > 0 ? (
        <p className="text-[10px] text-muted-foreground">
          {hiddenStagedCount} staged {hiddenStagedCount === 1 ? 'neighbour is' : 'neighbours are'}{' '}
          hidden by the filter.
        </p>
      ) : null}

      {/* List */}
      {visibleRows.length === 0 ? (
        <p className="rounded-md border border-border bg-muted/20 py-4 text-center text-xs text-muted-foreground">
          No neighbours match “{query}”.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {renderedRows.map(row => (
            <NeighborRowItem
              key={row.edgeId}
              row={row}
              staged={staged.has(row.neighbor.id)}
              onToggle={() => toggleStaged(row.neighbor.id)}
              legendLabels={legendLabels}
            />
          ))}
        </ul>
      )}

      {hiddenWindowCount > 0 ? (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setWindowCount(c => c + WINDOW_STEP)}
        >
          Show {Math.min(WINDOW_STEP, hiddenWindowCount)} more
        </Button>
      ) : null}
    </div>
  )
}

function NeighborRowItem({
  row,
  staged,
  onToggle,
  legendLabels,
}: {
  row: NeighborRow
  staged: boolean
  onToggle: () => void
  legendLabels?: LegendLabel[]
}) {
  const primaryLabel = row.neighbor.labels[0] ?? row.neighbor.type ?? 'Entity'
  const dirLabel = row.direction === 'outgoing' ? 'OUT' : 'IN'
  return (
    <li
      className={cn(
        'group flex items-center gap-3 rounded-lg border px-3 py-2 text-xs transition-colors',
        staged ? 'border-primary/50 bg-primary/10' : 'border-border bg-muted/20 hover:bg-muted/40'
      )}
    >
      <span
        className="inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
        style={labelStyle(primaryLabel, legendLabels)}
      >
        {primaryLabel}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium text-foreground" title={row.neighbor.name}>
            {row.neighbor.name}
          </span>
          <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
            {dirLabel}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
          <ArrowRight
            className={cn('h-2.5 w-2.5 shrink-0', row.direction === 'outgoing' ? '' : 'rotate-180')}
          />
          <span className="truncate font-mono">{row.edgeType}</span>
        </div>
      </div>
      <Button
        variant={staged ? 'default' : 'outline'}
        size="icon"
        className="h-7 w-7 shrink-0 max-md:h-9 max-md:w-9"
        onClick={onToggle}
        aria-pressed={staged}
        aria-label={staged ? 'Unstage neighbour' : 'Stage neighbour'}
      >
        <Plus
          className={cn('h-3.5 w-3.5 transition-transform', staged && 'rotate-45')}
          strokeWidth={2.5}
        />
      </Button>
    </li>
  )
}
